/**
 * Browser Zoom Detection Utilities
 *
 * These functions live here because inline script
 * entries like sendFedopsLoadStarted and handleBrowserZoom are bundled by rspack,
 * whose split-chunks config forcefully extracts any module with "thunderbolt-commons"
 * in its resolved path into a separate chunk. No-runtime inline entries cannot load
 * external chunks, so the code must reside in a safe package path.
 * This is temporary since we won't need it after we will stop using the zoom in BI events.
 */

const MOBILE_UA_REGEX = /Mobile|Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i
const IOS_UA_REGEX = /iPhone|iPad|iPod/i

export const isMobileUserAgent = (userAgent: string): boolean => {
	if (!userAgent) {
		return false
	}
	return MOBILE_UA_REGEX.test(userAgent)
}

export const isIOSDevice = (userAgent: string): boolean => {
	if (!userAgent) {
		return false
	}
	return IOS_UA_REGEX.test(userAgent)
}

export const isAndroidDevice = (userAgent: string): boolean => {
	if (!userAgent) {
		return false
	}
	return isMobileUserAgent(userAgent) && !isIOSDevice(userAgent)
}

export const isIOSChrome = (userAgent: string): boolean => {
	return isIOSDevice(userAgent) && userAgent.includes('Chrome')
}

export const detectRetinaScreen = (dpr: number, innerWidth: number, outerWidth: number): 1 | 2 => {
	if (!dpr || !innerWidth || !outerWidth) {
		return 1
	}
	return Math.trunc(dpr * innerWidth) <= outerWidth ? 1 : 2
}

export const detectBrowserZoomForDesktop = (dpr: number, innerWidth: number = 0, outerWidth: number = 0): number => {
	if (!dpr || !innerWidth || !outerWidth) {
		return 1
	}

	const displayDPR = detectRetinaScreen(dpr, innerWidth, outerWidth)
	if (!displayDPR) {
		return 1
	}

	if (dpr <= displayDPR) {
		return 1
	}

	return Math.round((dpr / displayDPR) * 100) / 100
}

type ZoomFactorWindow = {
	navigator?: { userAgent?: string }
	devicePixelRatio?: number
	innerWidth?: number
	outerWidth?: number
	visualViewport?: { scale?: number } | null
}

/**
 * Single source of truth for "what is the current browser zoom factor?"
 * Used by every zoom detection path in the codebase (pre-hydration inline scripts,
 * post-hydration runtime, BI reporting). Routes by device type:
 *
 * - iOS: visualViewport.scale (reliable at any time, including pre-hydration)
 * - Android (any non-iOS mobile): ratio of currentDpr vs stored baseline DPR
 *   (returns 1 when no baseline is stored — caller should treat as "no detection possible")
 * - Desktop: detectBrowserZoomForDesktop()
 *
 * `androidBaselineEnabled` gates the Android branch on the
 * browserZoomMobileOnloadDetection experiment. When false, Android short-circuits
 * to 1 even if a stale baseline lingers in localStorage from a prior on-session —
 * cancelling/removing the experiment must restore pre-experiment behavior, not
 * keep using leftover trails.
 *
 * Callers that need a boolean ("is the user zoomed?") should compare > 1 themselves.
 */
export const detectCurrentZoomFactor = (
	win: ZoomFactorWindow | null | undefined,
	androidBaselineEnabled: boolean = false
): number => {
	if (!win) {
		return 1
	}
	const userAgent = win.navigator?.userAgent || ''
	const devicePixelRatio = win.devicePixelRatio || 1

	if (isIOSDevice(userAgent)) {
		return win.visualViewport?.scale || 1
	}

	if (isAndroidDevice(userAgent)) {
		if (!androidBaselineEnabled) {
			return 1
		}
		const baseline = readBaselineDpr()
		if (!baseline) {
			return 1
		}
		return detectBrowserZoomFromBaseline(devicePixelRatio, baseline.dpr)
	}

	return detectBrowserZoomForDesktop(devicePixelRatio, win.innerWidth, win.outerWidth)
}

type WindowZoomInfo = {
	devicePixelRatio: number
	innerWidth: number
	outerWidth: number
	visualViewport?: { scale?: number } | null
}

export type ZoomParamsInfo = {
	devicePixelRatio: number
	innerWidth: number
	outerWidth: number
	visualViewportScale?: number
}

export const getZoomParamsInfo = (win: WindowZoomInfo): ZoomParamsInfo => {
	const scale = win.visualViewport?.scale
	return {
		devicePixelRatio: win.devicePixelRatio || 1,
		innerWidth: win.innerWidth,
		outerWidth: win.outerWidth,
		...(scale != null ? { visualViewportScale: scale } : {}),
	}
}

// --- Baseline DPR storage helpers (for Android zoom detection across MPA navigations) ---

const BASELINE_STORAGE_KEY = 'wix_dpr_baseline'

export type BaselineDpr = { dpr: number }

export const readBaselineDpr = (): BaselineDpr | null => {
	try {
		const rawStorage = localStorage.getItem(BASELINE_STORAGE_KEY)
		if (!rawStorage) {
			return null
		}
		const dpr = Number(rawStorage)
		return dpr > 0 ? { dpr } : null
	} catch {
		return null
	}
}

export const writeBaselineDpr = (dpr: number): void => {
	if (!(dpr > 0)) {
		return
	}
	try {
		localStorage.setItem(BASELINE_STORAGE_KEY, String(dpr))
	} catch {}
}

export const detectBrowserZoomFromBaseline = (currentDpr: number, baselineDpr: number): number => {
	if (!currentDpr || !baselineDpr || baselineDpr <= 0) {
		return 1
	}
	if (currentDpr <= baselineDpr) {
		return 1
	}
	return Math.round((currentDpr / baselineDpr) * 100) / 100
}
